import React, { useMemo } from 'react';
import { Box, Typography, styled } from '@mui/material';

function LinkedStepper({ linkedSteps, activeStepId, theme }) {
  const ecommerceSteps = useMemo(
    () => (linkedSteps || []).filter((s) => s.owner === 'ecommerce'),
    [linkedSteps],
  );
  const rsvpSteps = useMemo(
    () => (linkedSteps || []).filter((s) => s.owner !== 'ecommerce'),
    [linkedSteps],
  );

  const isRsvpPhase = rsvpSteps.some((s) => s.id === activeStepId);
  const ecommerceActiveIndex = ecommerceSteps.findIndex((s) => s.id === activeStepId);
  const rsvpActiveIndex = rsvpSteps.findIndex((s) => s.id === activeStepId);

  const ecommerceProgress = isRsvpPhase
    ? 1
    : ecommerceSteps.length > 1
      ? ecommerceActiveIndex / (ecommerceSteps.length - 1)
      : ecommerceActiveIndex > -1 ? 1 : 0;

  const rsvpProgress = !isRsvpPhase
    ? 0
    : rsvpSteps.length > 1
      ? rsvpActiveIndex / (rsvpSteps.length - 1)
      : rsvpActiveIndex > -1 ? 1 : 0;

  const primaryColor = theme?.palette?.primary?.main || '#1976d2';

  return (
    <PhaseBarWrapper>
      <PhaseSegment>
        <PhaseHeader>
          <PhaseLabel done={isRsvpPhase}>
            {isRsvpPhase && <PhaseCheck>✓</PhaseCheck>}
            Kauf
          </PhaseLabel>
          <PhaseHint done={isRsvpPhase}>
            {isRsvpPhase ? 'Abgeschlossen' : `Schritt ${Math.max(1, ecommerceActiveIndex + 1)} von ${ecommerceSteps.length}`}
          </PhaseHint>
        </PhaseHeader>
        <PhaseTrack>
          <PhaseFill style={{ width: `${Math.round(ecommerceProgress * 100)}%`, background: '#1a1a1a' }} />
        </PhaseTrack>
        <MiniStepper>
          {ecommerceSteps.map((step, idx) => {
            const done = isRsvpPhase || idx < ecommerceActiveIndex;
            const current = !isRsvpPhase && idx === ecommerceActiveIndex;
            return (
              <MiniStep key={step.id}>
                <MiniDot done={done} current={current} color="#1a1a1a">
                  {done ? '✓' : idx + 1}
                </MiniDot>
                <MiniLabel active={current}>{step.label}</MiniLabel>
                {idx < ecommerceSteps.length - 1 && (
                  <MiniConnector done={done} color="#1a1a1a" />
                )}
              </MiniStep>
            );
          })}
        </MiniStepper>
      </PhaseSegment>

      <PhaseDivider />

      <PhaseSegment>
        <PhaseHeader>
          <PhaseLabel>Anmeldung</PhaseLabel>
          <PhaseHint>
            {!isRsvpPhase
              ? 'Startet nach Zahlung'
              : `Schritt ${Math.max(1, rsvpActiveIndex + 1)} von ${rsvpSteps.length}`}
          </PhaseHint>
        </PhaseHeader>
        <PhaseTrack>
          <PhaseFill style={{ width: `${Math.round(rsvpProgress * 100)}%`, background: primaryColor }} />
        </PhaseTrack>
        <MiniStepper>
          {rsvpSteps.map((step, idx) => {
            const done = isRsvpPhase && idx < rsvpActiveIndex;
            const current = isRsvpPhase && idx === rsvpActiveIndex;
            return (
              <MiniStep key={step.id}>
                <MiniDot done={done} current={current} color={primaryColor}>
                  {done ? '✓' : idx + 1}
                </MiniDot>
                <MiniLabel active={current}>{step.label}</MiniLabel>
                {idx < rsvpSteps.length - 1 && (
                  <MiniConnector done={done} color={primaryColor} />
                )}
              </MiniStep>
            );
          })}
        </MiniStepper>
      </PhaseSegment>
    </PhaseBarWrapper>
  );
}

const PhaseBarWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: 0,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 2,
  overflow: 'hidden',
  marginBottom: theme.spacing(4),
}));

const PhaseSegment = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1.5, 2, 1.25),
}));

const PhaseDivider = styled(Box)(({ theme }) => ({
  width: '1px',
  background: theme.palette.divider,
  flexShrink: 0,
  alignSelf: 'stretch',
}));

const PhaseHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '6px',
});

const PhaseLabel = styled(Typography)(({ done }) => ({
  fontSize: '11px',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.07em',
  color: done ? 'inherit' : 'text.secondary',
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
}));

const PhaseCheck = styled('span')({
  fontSize: '10px',
  color: '#2e7d32',
  fontWeight: 700,
});

const PhaseHint = styled(Typography)({
  fontSize: '11px',
  color: 'text.secondary',
  component: 'span',
});

const PhaseTrack = styled(Box)(({ theme }) => ({
  height: '3px',
  borderRadius: '2px',
  background: theme.palette.divider,
  overflow: 'hidden',
  marginBottom: '10px',
}));

const PhaseFill = styled(Box)({
  height: '100%',
  borderRadius: '2px',
  transition: 'width 0.3s ease',
});

const MiniStepper = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 0,
  position: 'relative',
});

const MiniStep = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flex: 1,
  position: 'relative',
  minWidth: 0,
});

const MiniDot = styled(Box)(({ done, current, color }) => ({
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '10px',
  fontWeight: 500,
  flexShrink: 0,
  zIndex: 1,
  background: done ? color : current ? 'transparent' : 'transparent',
  color: done ? '#fff' : current ? color : '#bbb',
  border: done ? `1.5px solid ${color}` : current ? `1.5px solid ${color}` : '1.5px solid #ddd',
  transition: 'all 0.2s',
}));

const MiniLabel = styled(Typography)(({ active }) => ({
  fontSize: '10px',
  color: active ? 'text.primary' : 'text.disabled',
  fontWeight: active ? 500 : 400,
  textAlign: 'center',
  marginTop: '4px',
  lineHeight: 1.3,
  maxWidth: '60px',
  wordBreak: 'break-word',
  hyphens: 'auto',
}));

const MiniConnector = styled(Box)(({ done, color }) => ({
  position: 'absolute',
  top: '9px',
  left: 'calc(50% + 11px)',
  right: 'calc(-50% + 11px)',
  height: '1px',
  background: done ? color : '#ddd',
  transition: 'background 0.2s',
}));

export default LinkedStepper;
